import { useHeyApi } from "@platform/api";
import { proposalQueryKeys } from "@platform/mpx-quotes-grid";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface AddPlanRequest {
  carrierId: string;
  /**
   * Display name resolved for carrierId. BTX has no carrier directory, so
   * without this the proposal's carrier_name persists as the slug. Omitted
   * when unresolved (equal to the slug) — BTX falls back to carrierId anyway.
   */
  carrierName?: string;
  origin: "analyst";
  materialize: {
    rfpBenefitPlanGroupId: string;
    planPosition: number;
    network?: string;
  };
}

interface AddPlanVars {
  carrierId: string;
  carrierName: string;
  rfpBenefitPlanGroupId: string;
  network?: string;
  planPosition?: number;
}

interface UseAddPlanMutationOptions {
  rfpId: string | undefined;
  onSuccess?: () => void;
  onError?: (err: Error) => void;
}

interface UseAddPlanMutationResult {
  addPlan: (vars: AddPlanVars) => void;
  isAdding: boolean;
}

export const useAddPlanMutation = ({
  rfpId,
  onSuccess,
  onError,
}: UseAddPlanMutationOptions): UseAddPlanMutationResult => {
  const { centroPortalClient } = useHeyApi();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: async ({
      carrierId,
      carrierName,
      rfpBenefitPlanGroupId,
      network,
      planPosition = 1,
    }: AddPlanVars) => {
      if (!rfpId) {
        throw new Error("Missing rfpId");
      }

      const hasResolvedName = !!carrierName && carrierName !== carrierId;
      const body: AddPlanRequest = {
        carrierId,
        ...(hasResolvedName ? { carrierName } : {}),
        origin: "analyst",
        materialize: { rfpBenefitPlanGroupId, planPosition, network },
      };

      await centroPortalClient.post<unknown, unknown, true>({
        url: `/rfps/${encodeURIComponent(rfpId)}/proposals`,
        body,
        responseType: "json",
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      if (rfpId) {
        await queryClient.invalidateQueries({
          queryKey: proposalQueryKeys.all(rfpId),
        });
      }
      onSuccess?.();
    },
    onError: (err: Error) => {
      onError?.(err);
    },
  });

  return { addPlan: mutate, isAdding: isPending };
};
